import React, { useState, useEffect, useRef } from "react";
import FieldDropdown from "../common/InputField/FieldDropdown";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import { AccountPopupField, ACCOUNT_POPUP_MUTED_PANEL } from "./accountPopupFields";
import {
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminHeaderAction,
  AdminMainText,
  AdminMetadataText,
  AdminPageTitle,
  AdminStatusBadge,
  AdminSupportingText,
  adminWizardDropdownClasses,
  adminWizardInputClasses,
  adminVerificationDigitClasses,
  adminVerificationCodeGroupClassNames,
} from "../../sections/AdminDashboard/shared/adminUx";

const LoginSecurityPopup = ({ isOpen, onClose, setFieldValue, values = {}, errors = {}, initialStep = 0 }) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formValues, setFormValues] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [otpVerificationField, setOtpVerificationField] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(120); // 2 minutes in seconds
  const [showResend, setShowResend] = useState(false);

  // Update currentStep when initialStep changes (when popup opens with different field)
  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  // Initialize form values when popup opens
  useEffect(() => {
    if (isOpen) {
      const sampleData = {
        password: "",
        twoFactor: "Enabled",
        sessions: "2 devices",
        email: "john@acme.com.au",
        mobile: "0400 123 456"
      };
      
      setFormValues({
        password: values.password || "",
        twoFactor: values.twoFactor || sampleData.twoFactor,
        sessions: values.sessions || sampleData.sessions,
        email: values.email || sampleData.email,
        mobile: values.mobile || sampleData.mobile
      });
      
      // Reset OTP verification state only when popup first opens
      setShowOtpVerification(false);
      setShowConfirmation(false);
      setOtpVerificationField('');
      setOtp(['', '', '', '', '', '']);
      setTimer(120);
      setShowResend(false);
    }
  }, [isOpen]);

  // Separate effect for form values updates without resetting verification state
  useEffect(() => {
    if (isOpen && values) {
      const sampleData = {
        password: "",
        twoFactor: "Enabled",
        sessions: "2 devices",
        email: "john@acme.com.au",
        mobile: "0400 123 456"
      };
      
      setFormValues(prev => ({
        ...prev,
        password: values.password || "",
        twoFactor: values.twoFactor || sampleData.twoFactor,
        sessions: values.sessions || sampleData.sessions,
        email: values.email || sampleData.email,
        mobile: values.mobile || sampleData.mobile
      }));
    }
  }, [values]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timer > 0 && showOtpVerification) {
      interval = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
    } else if (timer === 0) {
      setShowResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, showOtpVerification]);

  const handleFieldChange = (field, value) => {
    setFormValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format timer to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle input change for individual digits
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste functionality
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    
    // Focus the next empty input or the last input
    const nextEmptyIndex = newOtp.findIndex(digit => digit === '');
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  // Resend OTP
  const handleResendOtp = () => {
    setTimer(120);
    setShowResend(false);
    setOtp(['', '', '', '', '', '']);
    // Add your resend OTP logic here
    console.log(`Resending OTP for ${otpVerificationField}`);
  };

  const steps = [
    {
      title: "New Password",
      description: "Update your account password for better security",
      field: "password",
      placeholder: "Enter new password",
      type: "password",
      required: true,
      showCurrentPassword: true,
      needsVerification: true
    },
    {
      title: "Configure Two-Factor Authentication",
      description: "Enable or manage two-factor authentication",
      field: "twoFactor",
      placeholder: "Select 2FA option",
      type: "select",
      options: [
        { label: "Enabled", value: "Enabled" },
        { label: "Disabled", value: "Disabled" }
      ],
      required: false
    },
    {
      title: "View Active Sessions",
      description: "Manage your active login sessions",
      field: "sessions",
      placeholder: "View active sessions",
      type: "sessions",
      required: false
    },
    {
      title: "Update Email Address",
      description: "Enter your new email address for account verification",
      field: "email",
      placeholder: "Enter new email address",
      type: "email",
      required: true,
      needsVerification: true
    },
    {
      title: "Update Mobile Number",
      description: "Enter your new mobile number for SMS verification",
      field: "mobile",
      placeholder: "Enter new mobile number",
      type: "tel",
      required: true,
      needsVerification: true
    }
  ];

  const handleSave = async () => {
    const fieldValue = formValues[currentStepData.field];
    
    if (currentStepData.needsVerification) {
      // Show OTP verification for password, email, and mobile
      setOtpVerificationField(currentStepData.field);
      setShowOtpVerification(true);
      setTimer(120);
      setShowResend(false);
      setOtp(['', '', '', '', '', '']);
      console.log(`OTP sent to ${currentStepData.field}:`, fieldValue);
    } else {
      console.log(`Saved ${currentStepData.field}:`, fieldValue);
      if (setFieldValue) {
        setFieldValue(currentStepData.field, fieldValue);
      }
      onClose();
    }
  };

  const handleOtpVerification = () => {
    const otpString = otp.join('');
    if (otpString.length === 6) {
      setIsVerifying(true);
      // Simulate OTP verification process
      setTimeout(() => {
        setIsVerifying(false);
        console.log(`OTP verified for ${otpVerificationField}:`, otpString);
        if (setFieldValue) {
          setFieldValue(otpVerificationField, formValues[otpVerificationField]);
        }
        setShowOtpVerification(false);
        setShowConfirmation(true);
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      }, 2000);
    }
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  if (!isOpen) return null;

  return (
    <Popup
      setTrigger={() => {}}
      ShowCrossButton={true}
      cross={true}
      onClose={onClose}
      businessDashboard={true}
      hideLogoInHeader={true}
    >
      <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
            <form>
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">

                  <div>
                    {showOtpVerification ? (
                      <div>
                        <div className="text-center mb-6">
                          <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                            Verify {otpVerificationField === "email"
                              ? "Email"
                              : otpVerificationField === "mobile"
                                ? "Mobile Number"
                                : "Password Change"}
                          </AdminPageTitle>
                          <AdminSupportingText className="text-mediumgray block text-center">
                            {otpVerificationField === "password"
                              ? "We've sent a 6-digit verification code to your registered email address."
                              : `We've sent a 6-digit verification code to ${formValues[otpVerificationField]}`}
                          </AdminSupportingText>
                        </div>
                        
                        <div className={`mb-4 ${adminVerificationCodeGroupClassNames(6)}`} onPaste={handlePaste}>
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (inputRefs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              className={adminVerificationDigitClasses()}
                              placeholder=""
                              aria-label={`Verification digit ${index + 1}`}
                            />
                          ))}
                        </div>
                        
                        <div className="text-center mb-4">
                          {timer > 0 ? (
                            <AdminMetadataText className="text-mediumgray">
                              Resend code in {formatTime(timer)}
                            </AdminMetadataText>
                          ) : (
                            <button
                              type="button"
                              onClick={handleResendOtp}
                              className="text-sm text-primaryblue hover:text-brightblue dashboard-text-main"
                            >
                              Resend code
                            </button>
                          )}
                        </div>
                      </div>
                    ) : showConfirmation ? (
                      <div className="text-center">
                        <div className="mb-6">
                          <div className="w-16 h-16 bg-freshgreen rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fi fi-rs-check text-white text-2xl"></i>
                          </div>
                          <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                            {otpVerificationField === "password"
                              ? "Password Updated!"
                              : otpVerificationField === "mobile"
                                ? "Mobile Number Updated!"
                                : "Information Updated!"}
                          </AdminPageTitle>
                          <AdminSupportingText className="text-mediumgray block text-center">
                            {otpVerificationField === "password"
                              ? "Your password has been successfully updated and is now secure."
                              : otpVerificationField === "mobile"
                                ? "Your mobile number has been successfully updated and verified."
                                : "Your information has been successfully updated."}
                          </AdminSupportingText>
                        </div>
                      </div>
                    ) : currentStepData.type === "select" ? (
                      <div>
                        <div className="space-y-1 w-full min-w-0">
                          <AdminDetailFieldLabel>{currentStepData.title}</AdminDetailFieldLabel>
                          <FieldDropdown
                            surface="muted"
                            fullWidth
                            value={formValues[currentStepData.field] || ""}
                            onChange={(e) => handleFieldChange(currentStepData.field, e.value)}
                            options={currentStepData.options}
                            placeholder="Select option"
                            className={adminWizardDropdownClasses(false, true)}
                            panelClassName={ACCOUNT_POPUP_MUTED_PANEL}
                            showClear={false}
                            appendTo={null}
                          />
                        </div>
                      </div>
                    ) : currentStepData.type === "sessions" ? (
                      <div>
                        <AdminDetailFieldLabel className="mb-2 block">
                          {currentStepData.title}
                        </AdminDetailFieldLabel>
                        <div className="space-y-3">
                          {/* Current Session */}
                          <div className="border border-lightgray rounded-lg p-4 bg-pastalblue">
                            <div className="flex items-center justify-between mb-2">
                              <AdminMainText className="text-freshgreen">Current Session</AdminMainText>
                              <AdminStatusBadge status="Active" label="Active" />
                            </div>
                            <div className="space-y-1">
                              <AdminMetadataText><strong>Device:</strong> Windows 10 - Chrome Browser</AdminMetadataText>
                              <AdminMetadataText><strong>Location:</strong> Sydney, Australia</AdminMetadataText>
                              <AdminMetadataText><strong>IP Address:</strong> 192.168.1.100</AdminMetadataText>
                              <AdminMetadataText><strong>Last Activity:</strong> 2 minutes ago</AdminMetadataText>
                            </div>
                          </div>

                          {/* Other Sessions */}
                          <div className="border border-lightgray rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <AdminMainText>Mobile Device</AdminMainText>
                              <AdminMetadataText className="text-mediumgray">2 hours ago</AdminMetadataText>
                            </div>
                            <div className="space-y-1">
                              <AdminMetadataText><strong>Device:</strong> iPhone 14 - Safari</AdminMetadataText>
                              <AdminMetadataText><strong>Location:</strong> Melbourne, Australia</AdminMetadataText>
                              <AdminMetadataText><strong>IP Address:</strong> 203.45.67.89</AdminMetadataText>
                              <AdminMetadataText><strong>Last Activity:</strong> 2 hours ago</AdminMetadataText>
                            </div>
                          </div>

                          <div className="border border-lightgray rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <AdminMainText>Tablet</AdminMainText>
                              <AdminMetadataText className="text-mediumgray">1 day ago</AdminMetadataText>
                            </div>
                            <div className="space-y-1">
                              <AdminMetadataText><strong>Device:</strong> iPad Pro - Safari</AdminMetadataText>
                              <AdminMetadataText><strong>Location:</strong> Brisbane, Australia</AdminMetadataText>
                              <AdminMetadataText><strong>IP Address:</strong> 198.51.100.42</AdminMetadataText>
                              <AdminMetadataText><strong>Last Activity:</strong> 1 day ago</AdminMetadataText>
                            </div>
                          </div>
                        </div>
                        <AdminMetadataText className="mt-3 block text-mediumgray">
                          Click Save to refresh session list or terminate inactive sessions
                        </AdminMetadataText>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {currentStepData.showCurrentPassword ? (
                          <div className="space-y-1">
                            <AdminDetailFieldLabel htmlFor="currentPassword">
                              Current Password
                            </AdminDetailFieldLabel>
                            <input
                              id="currentPassword"
                              type="password"
                              name="currentPassword"
                              placeholder="Enter current password"
                              onChange={(e) => handleFieldChange("currentPassword", e.target.value)}
                              value={formValues.currentPassword || ""}
                              className={adminWizardInputClasses(Boolean(errors?.currentPassword))}
                            />
                            {errors?.currentPassword ? (
                              <AdminFormFieldError>{errors.currentPassword}</AdminFormFieldError>
                            ) : null}
                          </div>
                        ) : null}
                        <AccountPopupField
                          step={{
                            title: currentStepData.title,
                            field: currentStepData.field,
                            inputType: currentStepData.type,
                            placeholder: currentStepData.placeholder,
                          }}
                          fieldKey={currentStepData.field}
                          value={formValues[currentStepData.field] || ""}
                          onChange={(v) => handleFieldChange(currentStepData.field, v)}
                          form={{ touched: {}, errors }}
                        />
                        {/* {currentStepData.needsVerification && (
                          <p className="text-xs text-blue-600 mt-2">
                            A verification code will be sent after saving
                          </p>
                        )} */}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Save Button */}
              {!showConfirmation && (
                <div className="flex justify-end w-full">
                  <div className="w-full mx-auto">
                    <div className="flex items-center justify-end mt-10 w-full">
                      <AdminHeaderAction
                        type="button"
                        onClick={showOtpVerification ? handleOtpVerification : handleSave}
                        disabled={isVerifying || (showOtpVerification && otp.join("").length !== 6)}
                      >
                        {isVerifying ? "Verifying" : showOtpVerification ? "Verify" : "Save"}
                      </AdminHeaderAction>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default LoginSecurityPopup;
